import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, Tooltip, LinearProgress, Tabs, Tab, Paper, Divider,
  Rating,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import SpaIcon from '@mui/icons-material/Spa';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import NailsIcon from '@mui/icons-material/AutoFixHigh';
import ManIcon from '@mui/icons-material/Man';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import {
  BEAUTY_SERVICE_PROVIDERS_QUERY, MY_BEAUTY_BOOKINGS_QUERY, MY_CEREMONIES_QUERY,
  CREATE_BEAUTY_BOOKING_MUTATION, UPDATE_BEAUTY_BOOKING_MUTATION, DELETE_BEAUTY_BOOKING_MUTATION,
} from '../graphql/queries';
import { useLov } from '../hooks/useLov';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const SERVICE_ICONS = { bridal_makeup:<FaceRetouchingNaturalIcon/>, mehendi:<SpaIcon/>, hairstyling:<ContentCutIcon/>, nail_art:<NailsIcon/>, groom_grooming:<ManIcon/>, full_package:<BookmarkIcon/> };
const STATUS_COLOR = { enquiry:'default', confirmed:'info', in_progress:'warning', completed:'success', cancelled:'error' };
const fmt = n => n!=null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

const EMPTY_BOOKING = { providerId:'', serviceType:'bridal_makeup', ceremonyId:'', eventDate:'', startTime:'', endTime:'', location:'', numPersons:1, agreedPrice:'', advancePaid:'', trialDate:'', specialRequests:'', notes:'' };

export default function BeautyServicesPage() {
  const [tab, setTab]   = useState(0);
  const [error, setError] = useState('');
  const [bookDialog, setBookDialog] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [bookForm, setBookForm]       = useState(EMPTY_BOOKING);
  const [filterType, setFilterType]   = useState('');
  const [confirmDel, setConfirmDel]   = useState(null);

  const { data: provData, loading: provLoad } = useQuery(BEAUTY_SERVICE_PROVIDERS_QUERY, {
    variables: filterType ? { serviceType: filterType } : {},
    fetchPolicy:'cache-and-network',
  });
  const { data: bookData, loading: bookLoad, refetch } = useQuery(MY_BEAUTY_BOOKINGS_QUERY, { fetchPolicy:'cache-and-network' });
  const { data: cerData }  = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy:'cache-and-network' });

  const providers  = provData?.beautyServiceProviders || [];
  const bookings   = bookData?.myBeautyBookings || [];
  const ceremonies = cerData?.myCeremonies || [];

  const { options: serviceTypeOpts } = useLov('beauty_service_type');

  const [createBooking] = useMutation(CREATE_BEAUTY_BOOKING_MUTATION, { onCompleted:()=>{ setBookDialog(false); setBookForm(EMPTY_BOOKING); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [updateBooking] = useMutation(UPDATE_BEAUTY_BOOKING_MUTATION, { onCompleted:()=>{ setBookDialog(false); setEditBooking(null); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [deleteBooking] = useMutation(DELETE_BEAUTY_BOOKING_MUTATION, { onCompleted:()=>{ setConfirmDel(null); refetch(); } });

  const openBook = (prov) => { setEditBooking(null); setBookForm({ ...EMPTY_BOOKING, providerId:prov.id }); setBookDialog(true); };
  const openEdit = (bk) => {
    setEditBooking(bk);
    setBookForm({ providerId:bk.providerId||'', serviceType:bk.serviceType||'bridal_makeup', ceremonyId:bk.ceremonyId||'', eventDate:bk.eventDate||'', startTime:bk.startTime||'', endTime:bk.endTime||'', location:bk.location||'', numPersons:bk.numPersons||1, agreedPrice:bk.agreedPrice||'', advancePaid:bk.advancePaid||'', trialDate:bk.trialDate||'', specialRequests:bk.specialRequests||'', notes:bk.notes||'' });
    setBookDialog(true);
  };
  const handleSave = () => {
    const vars = { ...bookForm, agreedPrice:bookForm.agreedPrice?parseFloat(bookForm.agreedPrice):undefined, advancePaid:bookForm.advancePaid?parseFloat(bookForm.advancePaid):undefined, numPersons:parseInt(bookForm.numPersons)||1, ceremonyId: bookForm.ceremonyId || null, providerId: bookForm.providerId || null };
    editBooking ? updateBooking({ variables:{ id:editBooking.id, ...vars } }) : createBooking({ variables:vars });
  };

  return (
    <Box sx={{ p:3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Beauty & Bridal Services</Typography>
          <Typography variant="body2" color="text.secondary">Mehendi, bridal makeup, hair styling & grooming — browse and book artists</Typography>
        </Box>
      </Stack>
      {error && <Alert severity="error" sx={{mb:2}} onClose={()=>setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{mb:3}}>
        <Tab label="Browse Artists"/>
        <Tab label={`My Bookings (${bookings.length})`}/>
      </Tabs>

      {tab===0 && (
        <Box>
          {/* Service type filter */}
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={1}>
            <Chip label="All" onClick={()=>setFilterType('')} color={!filterType?'primary':'default'} variant={!filterType?'filled':'outlined'}/>
            {serviceTypeOpts.map(t=>(
              <Chip key={t.value} icon={SERVICE_ICONS[t.value]} label={t.label} onClick={()=>setFilterType(t.value===filterType?'':t.value)}
                color={filterType===t.value?'primary':'default'} variant={filterType===t.value?'filled':'outlined'}/>
            ))}
          </Stack>
          {provLoad && <LinearProgress sx={{mb:2}}/>}
          {providers.length===0 && !provLoad && <Typography color="text.secondary" textAlign="center" mt={4}>No artists found for the selected filter.</Typography>}
          <Grid container spacing={2}>
            <Paged items={providers}>{rows => rows.map(p=>(
              <Grid item xs={12} sm={6} md={4} key={p.id}>
                <Card variant="outlined" sx={{height:'100%'}}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Typography fontWeight={700}>{p.name}</Typography>
                        {p.contactName && <Typography variant="body2" color="text.secondary">{p.contactName}</Typography>}
                        <Typography variant="caption" color="text.secondary">{p.city}</Typography>
                      </Box>
                      <Rating value={parseFloat(p.rating)||0} readOnly size="small" precision={0.5}/>
                    </Stack>
                    <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" gap={0.5}>
                      {(p.serviceTypes||[]).map(t=>(
                        <Chip key={t} icon={SERVICE_ICONS[t]} size="small" label={t.replace('_',' ')} variant="outlined"/>
                      ))}
                    </Stack>
                    {p.description && <Typography variant="body2" color="text.secondary" mt={1} sx={{WebkitLineClamp:2,display:'-webkit-box',overflow:'hidden',WebkitBoxOrient:'vertical'}}>{p.description}</Typography>}
                    <Divider sx={{my:1.5}}/>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary">Price Range</Typography>
                        <Typography variant="body2" fontWeight={600}>{fmt(p.priceMin)} – {fmt(p.priceMax)}</Typography>
                      </Box>
                      <Button variant="contained" size="small" onClick={()=>openBook(p)}>Book Now</Button>
                    </Stack>
                    {p.instagramUrl && (
                      <Typography variant="caption" color="primary.main" component="a" href={p.instagramUrl} target="_blank" sx={{mt:0.5,display:'block'}}>
                        View Portfolio
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </Box>
      )}

      {tab===1 && (
        <Box>
          {bookLoad && <LinearProgress sx={{mb:2}}/>}
          {bookings.length===0 && !bookLoad && <Typography color="text.secondary" textAlign="center" mt={4}>No bookings yet. Browse artists and click "Book Now".</Typography>}
          <Grid container spacing={2}>
            <Paged items={bookings}>{rows => rows.map(bk=>(
              <Grid item xs={12} sm={6} md={4} key={bk.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography fontWeight={700}>{bk.provider?.name||'Unknown'}</Typography>
                      <Chip size="small" label={bk.status} color={STATUS_COLOR[bk.status]||'default'}/>
                    </Stack>
                    <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
                      {SERVICE_ICONS[bk.serviceType]}
                      <Typography variant="body2">{(bk.serviceType||'').replace('_',' ')}</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">{bk.eventDate||'Date TBD'} {bk.startTime?`· ${bk.startTime}`:''}</Typography>
                    <Stack direction="row" justifyContent="space-between" mt={1}>
                      <Typography variant="caption">Agreed: {fmt(bk.agreedPrice)}</Typography>
                      <Typography variant="caption">Advance: {fmt(bk.advancePaid)}</Typography>
                    </Stack>
                    {bk.trialDate && <Chip size="small" label={`Trial: ${bk.trialDate}`} color={bk.trialDone?'success':'warning'} sx={{mt:0.5}}/>}
                    <Stack direction="row" spacing={0.5} mt={1}>
                      <IconButton size="small" onClick={()=>openEdit(bk)}><EditIcon fontSize="small"/></IconButton>
                      <IconButton size="small" color="error" onClick={()=>setConfirmDel(bk.id)}><DeleteIcon fontSize="small"/></IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </Box>
      )}

      {/* Booking Dialog */}
      <Dialog open={bookDialog} onClose={()=>setBookDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editBooking?'Edit Booking':'Book Beauty Service'}</DialogTitle>
        <DialogContent sx={{display:'flex',flexDirection:'column',gap:2,pt:2}}>
          <TextField select label="Provider" value={bookForm.providerId} onChange={e=>setBookForm(f=>({...f,providerId:e.target.value}))} fullWidth disabled={!!editBooking}>
            {providers.map(p=><MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </TextField>
          <TextField select label="Service Type *" value={bookForm.serviceType} onChange={e=>setBookForm(f=>({...f,serviceType:e.target.value}))} fullWidth>
            {serviceTypeOpts.map(t=><MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <TextField select label="Ceremony" value={bookForm.ceremonyId} onChange={e=>setBookForm(f=>({...f,ceremonyId:e.target.value}))} fullWidth>
            <MenuItem value="">— None —</MenuItem>
            {ceremonies.map(c=><MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Event Date" type="date" value={bookForm.eventDate} onChange={e=>setBookForm(f=>({...f,eventDate:e.target.value}))} fullWidth InputLabelProps={{shrink:true}}/></Grid>
            <Grid item xs={3}><TextField label="Start" type="time" value={bookForm.startTime} onChange={e=>setBookForm(f=>({...f,startTime:e.target.value}))} fullWidth InputLabelProps={{shrink:true}}/></Grid>
            <Grid item xs={3}><TextField label="End" type="time" value={bookForm.endTime} onChange={e=>setBookForm(f=>({...f,endTime:e.target.value}))} fullWidth InputLabelProps={{shrink:true}}/></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Agreed Price (₹)" type="number" value={bookForm.agreedPrice} onChange={e=>setBookForm(f=>({...f,agreedPrice:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={6}><TextField label="Advance Paid (₹)" type="number" value={bookForm.advancePaid} onChange={e=>setBookForm(f=>({...f,advancePaid:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <TextField label="Location / Venue" value={bookForm.location} onChange={e=>setBookForm(f=>({...f,location:e.target.value}))} fullWidth/>
          <TextField label="Trial Date" type="date" value={bookForm.trialDate} onChange={e=>setBookForm(f=>({...f,trialDate:e.target.value}))} fullWidth InputLabelProps={{shrink:true}}/>
          <TextField label="No. of Persons" type="number" value={bookForm.numPersons} onChange={e=>setBookForm(f=>({...f,numPersons:e.target.value}))} fullWidth/>
          <TextField label="Special Requests" value={bookForm.specialRequests} onChange={e=>setBookForm(f=>({...f,specialRequests:e.target.value}))} multiline rows={2} fullWidth/>
          <TextField label="Notes" value={bookForm.notes} onChange={e=>setBookForm(f=>({...f,notes:e.target.value}))} multiline rows={2} fullWidth/>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setBookDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!bookForm.providerId||!bookForm.serviceType}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete */}
      <Dialog open={!!confirmDel} onClose={()=>setConfirmDel(null)}>
        <DialogTitle>Cancel Booking?</DialogTitle>
        <DialogContent><Typography>This will permanently delete the booking.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmDel(null)}>Keep</Button>
          <Button variant="contained" color="error" onClick={()=>deleteBooking({variables:{id:confirmDel}})}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
